import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { blogPosts, blogCategories } from "../../mocks/blog";
import LanguageSelector, { LangCode, getTranslation } from "../../components/feature/LanguageSelector";

export default function Blog() {
  const [lang, setLang] = useState<LangCode>('es');
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todas");
  const [isScrolled, setIsScrolled] = useState(false);

  const t = (key: string) => getTranslation(lang, key);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const filteredPosts = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesSearch =
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === "Todas" || post.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const categoryLabel = (cat: string) => {
    if (cat === "Todas") return t('blog_cat_all');
    return cat;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-white shadow-md" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="https://static.readdy.ai/image/9fcc4035fe7cce5c6ba56d5b353fae35/5557e1bc3a7b24f13710d01af9330290.jpeg"
                alt="Red Lions Realty Logo"
                className="h-11 w-auto object-contain"
              />
            </Link>

            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className={`hover:text-red-600 transition-colors font-medium ${isScrolled ? 'text-gray-700' : 'text-gray-800'}`}>
                {t('nav_inicio')}
              </Link>
              <Link to="/#properties" className={`hover:text-red-600 transition-colors font-medium ${isScrolled ? 'text-gray-700' : 'text-gray-800'}`}>
                {t('nav_propiedades')}
              </Link>
              <div className="flex items-center space-x-4">
                <LanguageSelector currentLang={lang} onChangeLang={setLang} scrolled={isScrolled} />
                <a
                  href="/#contact"
                  className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-2.5 rounded-full hover:shadow-lg transition-all duration-300 font-medium whitespace-nowrap"
                >
                  {t('nav_cta')}
                </a>
              </div>
            </div>

            <button className="md:hidden text-gray-700">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-32 pb-16 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              {t('blog_page_title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('blog_page_sub')}
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                placeholder={t('blog_search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-gray-700 text-lg"
              />
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {blogCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2.5 rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg"
                    : "bg-white text-gray-700 border-2 border-gray-200 hover:border-red-500"
                }`}
              >
                {categoryLabel(category)}
              </button>
            ))}
          </div>

          {/* Results Counter */}
          <div className="text-center text-gray-600 mb-8">
            <span className="font-medium">
              {filteredPosts.length} {filteredPosts.length === 1 ? t('blog_results_singular') : t('blog_results_plural')}
            </span>
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-file-search-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-2xl font-semibold text-gray-700 mb-2">
              {t('blog_no_results_title')}
            </h3>
            <p className="text-gray-500">
              {t('blog_no_results_sub')}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-medium">
                      {post.category}
                    </span>
                    <span className="text-gray-500 text-sm flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {post.readTime}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center gap-2 text-red-600 font-medium group-hover:gap-3 transition-all">
                    <span>{t('blog_read_article')}</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="https://static.readdy.ai/image/9fcc4035fe7cce5c6ba56d5b353fae35/5557e1bc3a7b24f13710d01af9330290.jpeg"
                  alt="Red Lions Realty Logo"
                  className="h-12 w-auto object-contain"
                />
              </div>
              <p className="text-gray-400 leading-relaxed">
                {t('blog_footer_tagline')}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">{t('blog_footer_links')}</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                    {t('nav_inicio')}
                  </Link>
                </li>
                <li>
                  <Link to="/#propiedades" className="text-gray-400 hover:text-white transition-colors">
                    {t('nav_propiedades')}
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-gray-400 hover:text-white transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/#nosotros" className="text-gray-400 hover:text-white transition-colors">
                    {t('nav_nosotros')}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer_contact_label')}</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-start space-x-3">
                  <i className="ri-phone-line text-red-500 mt-1"></i>
                  <span>+34 744 780 446</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-mail-line text-red-500 mt-1"></i>
                  <span>redlionsl.24@gmail.com</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-map-pin-line text-red-500 mt-1"></i>
                  <span>Costa Blanca, Alicante, España</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">{t('blog_footer_follow')}</h3>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/redlionsrealty"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a
                  href="https://www.tiktok.com/@red.lions.realty"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  <i className="ri-tiktok-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>{t('footer_rights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
